<template>
    <div>
        <div class="flex gap-2 items-center mb-4">
            <div @click="getData"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold cursor-pointer py-2 px-4 rounded flex items-center">
                <svg class="fill-current w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                    <path
                        d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z" />
                </svg>
                <span>{{ $t('Get Data') }}</span>
            </div>
            <div v-if="gettingData && resumeProgress == true" @click="resumeProgress = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 h-full font-bold cursor-pointer py-2 px-4 rounded flex items-center">
                <svg class="fill-current w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                    <path
                        d="M48 64C21.5 64 0 85.5 0 112V400c0 26.5 21.5 48 48 48H80c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H48zm192 0c-26.5 0-48 21.5-48 48V400c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H240z" />
                </svg>
            </div>
            <div v-else-if="gettingData" @click="resumeProgress = true; getData()"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 h-full font-bold cursor-pointer py-2 px-4 rounded flex items-center">
                <svg class="fill-current w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 384 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                    <path
                        d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z" />
                </svg>
            </div>
            <span v-if="gettingData" class="text-xs text-red-400">
                {{ $t("Do not forget to check the results and press save when data is loaded") }}!
            </span>
            <div class="text-xs text-gray-700 flex flex-col gap-2 justify-center h-full">
                <span class="text-xs text-gray-700">{{ $t("Downloaded data from") }} {{ gottenData }} {{ $t('events')
                }}</span>
                <span class="text-xs text-gray-700">{{ $t("Manual update on") }} {{ customUpdates }} {{ $t('events')
                }}</span>
            </div>
        </div>
        <div v-if="gettingData"
            class="mb-4 bg-gray-50 rounded-xl p-4 flex items-center justify-center relative overflow-hidden">
            <div class="absolute h-full top-0 left-0 bg-primary-100 rounded-xl" id='loadingIndication'
                :style="'width:' + progress + '%;'">
            </div>
            {{ Math.round(progress) }}%
        </div>
        <div class="text-xs mb-4 text-gray-700 flex flex-col gap-2">
            <span class="text-xs text-gray-700">
                {{ getBytes(Math.round(JSON.stringify(data).replace(/[\[\]\,\"]/g, '').length)) }}</span>
            <span class="text-xs text-gray-700">
                {{ getBytes(Math.round(JSON.stringify(data).replace(/[\[\]\,\"]/g, '').length + (450000 * gottenData))) }}
                (This is an estimate inclduing image sizes)</span>
        </div>

        <div v-if="missingLocations.length > 0" class="text-xs mb-4 text-red-500 flex flex-col gap-2">
            {{ missingLocations.length }} {{ $t('Missing locations') }}: {{ missingLocations.join(',') }}
        </div>

        <form :action="submitURL" method="post" enctype="multipart/form-data">
            <table class="min-w-full divide-y divide-gray-200 overflow-auto table-fixed">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="py-3.5 px-4 text-sm w-[10%] font-normal text-left rtl:text-right text-gray-800">
                            {{ $t('Add') }}
                        </th>
                        <th class="py-3.5 px-4 text-sm w-[30%] font-normal text-left rtl:text-right text-gray-800">
                            {{ $t('Name') }}
                        </th>
                        <th class="py-3.5 px-4 text-sm w-[10%] font-normal text-left rtl:text-right text-gray-800">
                            {{ $t('Dates') }}
                        </th>
                        <th class="py-3.5 px-4 text-sm w-[10%] font-normal text-left rtl:text-right text-gray-800">

                        </th>
                        <th class="py-3.5 px-4 text-sm w-[30%] font-normal text-left rtl:text-right text-gray-800">
                            {{ $t('Progress') }}
                        </th>
                        <th class="py-3.5 px-4 text-sm w-[10%] font-normal text-left rtl:text-right text-gray-800">

                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <template v-for="(item, key) in data">
                        <tr>
                            <input type="hidden" :id="'additem[' + key + '][url]'" :name="'additem[' + key + '][url]'"
                                   class="js_item_url" :value="item.external_url" :data-key="key" :data-start="item.start">
                            <td class="px-4 py-4 text-sm font-medium whitespace-nowrap max-w-[30px]">
                                <div>
                                    <p class="text-sm font-normal text-gray-600 truncate">
                                        <input v-if="item.existingObject" type="checkbox" :id="'additem[' + key + ']'"
                                            :name="'additem[' + key + ']'" />
                                        <input v-else type="checkbox" :id="'additem[' + key + ']'"
                                            :name="'additem[' + key + ']'" checked />
                                    </p>
                                </div>
                            </td>
                            <td class="px-4 py-4 text-sm font-medium whitespace-nowrap">
                                <div>
                                    <h2 class="font-medium text-gray-800 truncate max-w-[300px]">{{ item.name }}</h2>
                                </div>
                            </td>

                            <td class="px-4 py-4 text-sm whitespace-nowrap">
                                <div>
                                    <h4 class="text-gray-700">
                                        {{ getDate(item.start) }}
                                    </h4>
                                    <p class="text-sm font-normal text-gray-600 truncate">
                                        {{ getTime(item.start) }}
                                    </p>
                                </div>
                            </td>
                            <td class="px-4 py-4 text-sm font-medium whitespace-nowrap">
                                <span v-if="item.existingObject"
                                    class="bg-green-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded">
                                    {{ $t("already exists") }}!
                                </span>
                            </td>
                            <td class="px-4 py-4 text-sm font-medium whitespace-nowrap">
                                <div>
                                    <div class="text-xs flex gap-1 items-center">
                                        <div v-if="Object.keys(item).includes('description') && Object.keys(item.description).length > 0"
                                            class="w-2 h-2 rounded-full bg-green-400"></div>
                                        <div v-else-if="Object.keys(item).includes('description') && Object.keys(item.description).length == 0"
                                            class="w-2 h-2 rounded-full bg-red-400"></div>
                                        <div v-else class="w-2 h-2 rounded-full bg-gray-300"></div>
                                        <span class="text-xs">
                                            {{ $t('Description') }}
                                        </span>
                                    </div>

                                    <div class="text-xs flex gap-1 items-center">
                                        <div v-if="Object.keys(item).includes('start') && Object.keys(item).includes('end') && item.start && item.end"
                                            class="w-2 h-2 rounded-full bg-green-400"></div>
                                        <div v-else-if="Object.keys(item).includes('start') && Object.keys(item).includes('end')"
                                            class="w-2 h-2 rounded-full bg-red-400"></div>
                                        <div v-else class="w-2 h-2 rounded-full bg-gray-300"></div>
                                        <span class="text-xs">
                                            {{ $t('Dates') }}
                                        </span>
                                    </div>
                                    <div class="text-xs flex gap-1 items-center">
                                        <div v-if="Object.keys(item).includes('categories') && item.categories"
                                            class="w-2 h-2 rounded-full bg-green-400"></div>
                                        <div v-else-if="Object.keys(item).includes('categories')"
                                            class="w-2 h-2 rounded-full bg-red-400"></div>
                                        <div v-else class="w-2 h-2 rounded-full bg-gray-300"></div>
                                        <span class="text-xs">
                                            {{ $t('Categories') }}
                                        </span>
                                    </div>

                                    <div class="text-xs flex gap-1 items-center">
                                        <div v-if="Object.keys(item).includes('price') && (item.price ||item.price == 0)"
                                            class="w-2 h-2 rounded-full bg-green-400"></div>
                                        <div v-else-if="Object.keys(item).includes('price')"
                                            class="w-2 h-2 rounded-full bg-red-400"></div>
                                        <div v-else class="w-2 h-2 rounded-full bg-gray-300"></div>
                                        <span class="text-xs">
                                            {{ $t('Price') }}
                                        </span>
                                    </div>
                                    <div class="text-xs flex gap-1 items-center">
                                        <div v-if="Object.keys(item).includes('location') && item.location"
                                            class="w-2 h-2 rounded-full bg-green-400"></div>
                                        <div v-else-if="Object.keys(item).includes('location')"
                                            class="w-2 h-2 rounded-full bg-red-400"></div>
                                        <div v-else class="w-2 h-2 rounded-full bg-gray-300"></div>
                                        <span class="text-xs">
                                            {{ $t('Location') }}
                                        </span>
                                    </div>
                                </div>
                            </td>

                            <td class="px-4 py-4 text-sm whitespace-nowrap gap-2 items-center table-cell">
                                <div @click="getSpecificData(key)" class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold cursor-pointer py-2 px-4 rounded inline-flex items-center"
                                        :class="[item.loadingStatus == 'loading' ? 'bg-orange-400' : item.loadingStatus == 'loaded' ? 'bg-green-400' : '']">
                                    <svg v-if="!Object.keys(item).includes('loadingStatus')" class="fill-current w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                        <path d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z" />
                                    </svg>
                                    <div v-else-if="item.loadingStatus === 'loading'" role="status">
                                        <svg aria-hidden="true" class="w-4 h-4 me-3 text-gray-800 animate-spin fill-gray-300" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                        </svg>
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                    <svg v-else-if="item.loadingStatus === 'loaded'" class="fill-current w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                        <path d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z" />
                                    </svg>
                                </div>

                                <div @click="saveSpecificData(key)"
                                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold cursor-pointer py-2 px-4 rounded inline-flex relative items-center group"
                                    :class="[item.savingStatus == 'saved' ? 'bg-green-400' : item.savingStatus == 'error' ? ' bg-red-400' : '']">
                                    <svg v-if="!Object.keys(item).includes('savingStatus')"
                                        class="fill-current w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                                        <path
                                            d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
                                    </svg>
                                    <div v-else-if="item.savingStatus == 'saving'" role="status">
                                        <svg aria-hidden="true"
                                            class="w-4 h-4 me-3 text-gray-800 animate-spin fill-gray-300"
                                            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="currentColor" />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentFill" />
                                        </svg>
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                    <svg v-else-if="item.savingStatus == 'saved'" class="w-4 h-4 me-2 text-white flex-shrink-0"
                                        aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                                        viewBox="0 0 20 20">
                                        <path
                                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                                    </svg>
                                    <svg v-else-if="item.savingStatus == 'error'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 me-2 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 384 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                                    <svg class="fill-current w-4 h-4 hidden group-hover:block absolute bg-gray-400" xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                                        <path
                                            d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
                                    </svg>
                                </div>
                                <div @click="openCurrent(key)"
                                    class="hover:bg-gray-200 text-gray-800 font-bold cursor-pointer py-2 px-4 rounded inline-flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 transition"
                                        :class="[item.accordionOpen ? 'rotate-180' : 'rotate-0']" fill="currentColor"
                                        viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
                                        <path
                                            d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
                                    </svg>
                                </div>
                            </td>
                        </tr>
                        <tr v-show="item.accordionOpen" :class="[item.accordionOpen ? 'table-row' : 'hidden']">
                            <td colspan="7">
                                <div class="bg-gray-100 max-w-screen py-4">
                                    <div class="w-full max-w-screen-md mx-auto p-4 bg-white rounded-xl flex flex-col gap-2">
                                        <div class="">
                                            <label :for="'events[' + key + '][name]'"
                                                class="block font-medium text-sm text-primary-darkest">{{ $t("Name") }} *
                                            </label>
                                            <input :id="'events[' + key + '][name]'" :name="'events[' + key + '][name]'"
                                                type="text" :autocomplete="'events[' + key + '][name]'"
                                                :value="item.name ?? ''"
                                                class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                        </div>
                                        <div class="">
                                            <label :for="'events[' + key + '][external_url]'"
                                                class="block font-medium text-sm text-primary-darkest">{{ $t("External URL")
                                                }}
                                                * </label>
                                            <input :id="'events[' + key + '][external_url]'"
                                                :name="'events[' + key + '][external_url]'" type="url"
                                                :autocomplete="'events[' + key + '][external_url]'"
                                                :value="item.external_url ?? ''"
                                                class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                        </div>
                                        <div class="flex gap-2 w-full">
                                            <div class="w-full">
                                                <label :for="'events[' + key + '][start]'"
                                                    class="block font-medium text-sm text-primary-darkest">{{ $t("Start") }}
                                                    *
                                                </label>
                                                <input :id="'events[' + key + '][start]'"
                                                    :name="'events[' + key + '][start]'" type="datetime-local"
                                                    :autocomplete="'events[' + key + '][start]'"
                                                    :value="item.start ? getDateTime(item.start) : ''"
                                                    class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                            </div>
                                            <div class="w-full">
                                                <label :for="'events[' + key + '][end]'"
                                                    class="block font-medium text-sm text-primary-darkest">{{ $t("End") }} *
                                                </label>
                                                <input :id="'events[' + key + '][end]'" :name="'events[' + key + '][end]'"
                                                    type="datetime-local" :autocomplete="'events[' + key + '][end]'"
                                                    :value="item.end ? getDateTime(item.end) : ''"
                                                    class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                            </div>
                                        </div>
                                        <div class="flex gap-2 w-full items-center">
                                            <div class="w-full">
                                                <label :for="'events[' + key + '][price]'"
                                                    class="block font-medium text-sm text-primary-darkest">{{ $t("Price") }}
                                                    *
                                                </label>
                                                <input :id="'events[' + key + '][price]'"
                                                    :name="'events[' + key + '][price]'" type="number"
                                                    :autocomplete="'events[' + key + '][price]'" :value="item.price ?? ''"
                                                    class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                            </div>
                                            <div class="w-full flex gap-2 pt-4">
                                                <input :id="'events[' + key + '][sold_out]'"
                                                    :name="'events[' + key + '][sold_out]'" type="checkbox"
                                                    :autocomplete="'events[' + key + '][sold_out]'"
                                                    :checked="typeof item.sold_out !== 'undefined' ? item.sold_out : false"
                                                    class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block">
                                                <label :for="'events[' + key + '][sold_out]'"
                                                    class="block font-medium text-sm text-primary-darkest">{{ $t("Sold out")
                                                    }}*
                                                </label>
                                            </div>
                                        </div>
                                        <div class="">
                                            <div v-if="Object.keys(item).includes('location') && item.location && item.location.length > 0"
                                                class="text-xs text-green-500">
                                                {{ $t('Location matched with') }}: {{ item.location[0].name.en }}
                                            </div>
                                            <div v-else-if="item.locationtext" class="text-xs text-gray-400">
                                                {{ $t('Location from event but not matched') }}: <span
                                                    class="text-red-500 text-xs">{{ item.locationtext }}</span>
                                            </div>
                                            <input :id="'events[' + key + '][location]'"
                                                :name="'events[' + key + '][location]'" readonly type="hidden"
                                                :autocomplete="'events[' + key + '][location]'"
                                                :value="item.location ? item.location.id : ''"
                                                class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm bg-neutral-100 border-gray-300 cursor-not-allowed mt-1 block w-full">
                                        </div>
                                        <div class="">
                                            <label :for="'events[' + key + '][image_url]'"
                                                class="block font-medium text-sm text-primary-darkest">{{ $t("image url") }}
                                                * </label>
                                            <input :id="'events[' + key + '][image_url]'"
                                                :name="'events[' + key + '][image_url]'" type="text"
                                                :autocomplete="'events[' + key + '][image_url]'"
                                                :value="item.image_url ?? ''"
                                                class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                        </div>
                                        <div class="">
                                            <label :for="'events[' + key + '][description]'"
                                                class="block font-medium text-sm text-primary-darkest">{{ $t("Description") }}
                                                * </label>
                                            <small>
                                                {{ $t("If you want to make changes") }},
                                                {{ $t("please do so after scraping") }}</small>
                                            <input :id="'events[' + key + '][description]'"
                                                :name="'events[' + key + '][description]'" readonly type="text"
                                                :autocomplete="'events[' + key + '][description]'"
                                                :value="JSON.stringify(item.description) ?? ''"
                                                class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm bg-neutral-100 border-gray-300 cursor-not-allowed mt-1 block w-full">
                                        </div>
                                        <div>
                                            <template v-for="(eventitems, key) in item.eventitems">
                                                <div class="flex gap-2 w-full">
                                                    <div class="w-full">
                                                        <label :for="'events[' + key + '][start_date]'"
                                                               class="block font-medium text-sm text-primary-darkest">{{ $t("Start") }} *
                                                        </label>
                                                        <input :id="'events[' + key + '][start_date]'" :name="'events[' + key + '][start_date]'"
                                                               type="datetime-local" :autocomplete="'events[' + key + '][end]'"
                                                               :value="eventitems.start_date ? getDateTime(eventitems.start_date) : ''"
                                                               class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                                    </div>

                                                    <div class="w-full">
                                                        <label :for="'events[' + key + '][end_date]'"
                                                               class="block font-medium text-sm text-primary-darkest">{{ $t("End") }}
                                                            *
                                                        </label>
                                                        <input :id="'events[' + key + '][end_date]'"
                                                               :name="'events[' + key + '][end_date]'" type="datetime-local"
                                                               :autocomplete="'events[' + key + '][end_date]'"
                                                               :value="eventitems.end_date ? getDateTime(eventitems.end_date) : ''"
                                                               class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                                    </div>


                                                    <div class="w-full">
                                                        <label :for="'events[' + key + '][price]'"
                                                               class="block font-medium text-sm text-primary-darkest">{{ $t("Price") }} *
                                                        </label>
                                                        <input :id="'events[' + key + '][price]'" :name="'events[' + key + '][price]'"
                                                               type="number" :autocomplete="'events[' + key + '][price]'"
                                                               :value="eventitems.price"
                                                               class="border-gray-300 focus:border-primary-lighter focus:ring-primary-lighter rounded-md shadow-sm mt-1 block w-full">
                                                    </div>

                                                    <div class="flex items-center">
                                                        <input :id="'events[' + key + '][sold_out]'" :name="'events[' + key + '][sold_out]'" type="checkbox" :checked="eventitems.sold_out"
                                                               class="w-[42px] h-[42px] text-primary bg-gray-100 border-gray-300 rounded-lg focus:ring-primary dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600">
                                                    </div>

                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </form>
    </div>
</template>

<script>
import moment from "moment";
import axios from 'axios'

export default {
    data() {
        return {
            data: JSON.parse(this.$attrs.data) ?? [],
            progress: 0,
            gottenData: 0,
            gettingData: false,
            resumeProgress: true,
            customUpdates: 0,
            missingLocations: [],
            submitURL: this.$attrs.submiturl ?? '',
        };
    },
    methods: {
        getDate(date) {
            var dateTime = _.clone(date);
            return moment.utc(dateTime).format('DD/MM/YYYY');
        },

        getDateTime(date) {
            return moment.utc(date).format('YYYY-MM-DDTHH:mm');
        },

        getTime(date) {
            var dateTime = _.clone(date);
            return moment.utc(dateTime).format('LT');
        },

        setTime(datetime, time) {
            if (time && time.split(':').length == 2) {
                var hours = parseInt(time.split(":")[0]);
                var minutes = parseInt(time.split(":")[1]);
            } else if (time && time.split(".").length == 2) {
                var hours = parseInt(time.split(".")[0]);
                var minutes = parseInt(time.split(".")[1]);
            } else {
                return datetime
            }
            var newDatetime = moment.utc(datetime).set("hour", hours).set("minute", minutes);

            return newDatetime.format('YYYY-MM-DDTHH:mm');
        },

        openCurrent(key) {
            this.data[key]['accordionOpen'] = this.data[key]['accordionOpen'] ? !this.data[key]['accordionOpen'] : true;
            this.customUpdates++;
        },

        getBytes(bytes) {
            const decimals = 2;
            if (!+bytes) return '0 Bytes'

            const k = 1024
            const dm = decimals < 0 ? 0 : decimals
            const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

            const i = Math.floor(Math.log(bytes) / Math.log(k))

            return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
        },

        async makePostCall(url, data, key) {
            var _this = this;
            axios({
                method: 'post',
                url: url,
                data: {
                    data: data,
                }
            }).then(function (response) {
                _this.data[key]['savingStatus'] = 'saved';
                _this.customUpdates++;
            })
                .catch(function (error) {
                    _this.data[key]['savingStatus'] = 'error';
                    _this.customUpdates++;
                    console.log(error);
                });
        },

        async makeCall(url) {
            try {
                let response = await fetch(
                    url,
                    {
                        headers: {
                            "X-Requested-With": "XMLHttpRequest",
                        },
                    }
                );
                return response.json();
            } catch (error) {
                this.resumeProgress = false;
                console.log(error);
                return 0;
            }
        },

        async saveSpecificData(key) {
            this.data[key]['savingStatus'] = "saving";
            this.customUpdates++;
            await this.makePostCall(this.submitURL, this.data[key], key);
        },

        async getSpecificData(key) {

            this.data[key]['loadingStatus'] = "loading";
            this.customUpdates++;

            var elements = document.getElementsByClassName("js_item_url");
            var url = elements[key].value;

            let scrapeUrl = `${this.$baseUrl}/vimata/scraper/scrapePage?scrape_url=${url}&name=${this.data[key]['name']}&start=${elements[key].dataset.start} ${this.data[key]['genre'] ? "&genre=" + this.data[key]['genre'] : ""}`;
            let response = await this.makeCall(scrapeUrl);
            if (!response) return 0;
            if (response.name && response.name != this.data[key]['name']) {
                this.data[key]['name'] = response.name;
            }

            if (response.description && response.description != this.data[key]['description']) {
                this.data[key]['description'] = response.description;
            }

            if (response.startTime && this.getTime(response.start) != response.startTime) {
                this.data[key]['start'] = this.setTime(response.start, response.startTime)
            }

            if (response.image_url  && response.image_url != this.data[key]['image_url']) {
                this.data[key]['image_url'] = response.image_url;
            }

            if (response.start && response.start != this.data[key]['start']) {
                this.data[key]['start'] = response.start;
            }

            if (response.end && response.end != this.data[key]['end']) {
                this.data[key]['end'] = response.end;
            }

            if (response.endTime && this.getTime(response.end) != response.endTime) {
                this.data[key]['end'] = this.setTime(response.end, response.endTime)
            }

            if (response.price && response.price != this.data[key]['price']) {
                this.data[key]['price'] = response.price;
            }

            if (response.existingObject && response.existingObject != this.data[key]['existingObject']) {
                this.data[key]['existingObject'] = response.existingObject;
            }

            if (response.location && response.location != this.data[key]['location']) {
                this.data[key]['location'] = response.location;
            }

            if (response.locationtext && response.locationtext != this.data[key]['locationtext']) {
                this.data[key]['locationtext'] = response.locationtext;
            }

            if (!response.location || response.location.length == 0) {
                if (!this.missingLocations.includes(response.locationtext)) {
                    this.missingLocations.push(response.locationtext ?? null);
                    if (!response.locationtext) {
                        console.log("Something went wrong with " + this.data[key]['name']);
                    }
                }
            }

            if (response.sold_out && response.sold_out != this.data[key]['sold_out']) {
                this.data[key]['sold_out'] = response.sold_out;
            }

            if (response.categories && response.categories != this.data[key]['categories']) {
                this.data[key]['categories'] = response.categories;
            }

            this.data[key]['eventitems'] = response.eventitems;

            this.data[key]['existingObject'] = response.existingObject;
            this.customUpdates++;

            this.data[key]['loadingStatus'] = "loaded";
            this.customUpdates++;
        },

        async getData() {
            this.gettingData = true;
            var elements = document.getElementsByClassName("js_item_url");

            for (var i = this.gottenData; i < elements.length; i++) {
                if (this.resumeProgress == true) {
                    var response = await this.makeCall(this.$baseUrl + "/vimata/scraper/scrapePage?scrape_url=" + elements[i].value + "&start=" + elements[i].dataset.start);
                    if (!response) continue;
                    this.data[elements[i].dataset.key]['name'] = response.name;
                    this.data[elements[i].dataset.key]['description'] = response.description;
                    this.data[elements[i].dataset.key]['start'] = response.start;
                    this.data[elements[i].dataset.key]['end'] = response.end;
                    if (this.getTime(response.start) != response.startTime) {
                        this.data[elements[i].dataset.key]['start'] = this.setTime(response.start, response.startTime)
                    }
                    if (this.getTime(response.end) != response.endTime) {
                        this.data[elements[i].dataset.key]['end'] = this.setTime(response.end, response.endTime)
                    }
                    this.data[elements[i].dataset.key]['price'] = response.price;
                    this.data[elements[i].dataset.key]['existingObject'] = response.existingObject;
                    this.data[elements[i].dataset.key]['locationtext'] = response.locationtext;
                    this.data[elements[i].dataset.key]['location'] = response.location;
                    if (!response.location || response.location.length == 0) {
                        if (!this.missingLocations.includes(response.locationtext)) {
                            this.missingLocations.push(response.locationtext ?? null);
                            if (!response.locationtext) {
                                console.log("Something went wrong with " + this.data[elements[i].dataset.key]['name']);
                            }
                        }
                    }
                    this.data[elements[i].dataset.key]['sold_out'] = response.sold_out;
                    this.data[elements[i].dataset.key]['eventitems'] = false;
                    this.progress = (i / elements.length) * 100;
                    this.gottenData = i;
                    setTimeout(function () { }, 200);
                }
            }
        }
    },
};
</script>
